import { z } from 'zod';
import { BILLING_INTERVALS, PAID_PLAN_IDS } from './account';
import { ACTIVITY_SOURCES, AUDIENCES, SIGN_IN_METHODS } from './admin';
import { DESKTOP_PLATFORMS } from './desktop-installers';
import { CHANNELS, DESKTOP_SYSTEMS, PLANS_OPENERS, VERSION_ACTIONS } from './events';
import { RewritesSchema } from './rewrite';

// The admin's live view and an account's journey: read from the raw events, kept 90 days.

const count = z.int().nonnegative();
const at = z.iso.datetime();

/**
 * What something was done on: a browser and its operating system, or a desktop app version and
 * its system. Empty where it isn't known.
 */
export const UsedOnSchema = z.object({
  name: z.string().describe('A browser, such as "chrome", or the desktop app’s version.'),
  system: z.string().describe('An operating system, such as "macos".'),
});
export type UsedOn = z.infer<typeof UsedOnSchema>;

/** Where someone active now is: a page of the site, the desktop app, or an API key. */
export const LIVE_PLACES = [
  'home',
  'studio',
  'pricing',
  'how-it-works',
  'desktop-apps',
  'desktop',
  'api',
] as const;
export type LivePlace = (typeof LIVE_PLACES)[number];

/** One thing someone did, without the text or anything that names them. */
export const LiveEventSchema = z.object({
  at,
  event: z.string().describe('The kind, as the events are kept, such as "rewrite".'),
  item: z.string(),
  variant: z.string(),
  outcome: z.string(),
  amount: count.describe('Words, for a rewrite.'),
  durationMs: count,
  channel: z.string().describe('How a visit came, such as "search"; empty for anything else.'),
  source: z.enum(ACTIVITY_SOURCES),
  plan: z
    .enum(AUDIENCES)
    .nullable()
    .describe('The plan they have the use of, "visitor" if not signed in; null if not known.'),
  country: z.string().describe('Two letters, or empty if not known.'),
  client: UsedOnSchema.nullable(),
});
export type LiveEvent = z.infer<typeof LiveEventSchema>;

export const AdminLiveSchema = z.object({
  generatedAt: at,
  tracksVisitors: z.boolean(),
  active: z
    .object({
      people: count.describe('People who did anything in the last 5 minutes.'),
      sources: z
        .record(z.enum(ACTIVITY_SOURCES), count)
        .describe('The same people, by where they last did something.'),
      minutes: z
        .array(count)
        .describe('People who did anything in each of the last 30 minutes, oldest first.'),
      places: z.record(z.enum(LIVE_PLACES), count).describe('Where those active now last were.'),
    })
    .nullable()
    .describe('Null unless visitors are told apart.'),
  lastHour: z.object({
    opens: count.describe('Times anyone opened the site, as Traffic counts them.'),
    rewrites: count.describe('Rewrites completed.'),
    failed: count.describe('Rewrites that failed.'),
    slow: count.describe('Rewrites that took more than 10 seconds.'),
    limited: count.describe('Requests turned away by a credit limit.'),
    signups: count.describe('Accounts made.'),
    subscribed: count.describe('Subscriptions started.'),
  }),
  feed: z.array(LiveEventSchema).describe('The latest things done, newest first.'),
});
export type AdminLive = z.infer<typeof AdminLiveSchema>;

const firstVisit = z.object({
  kind: z.literal('first-visit'),
  at,
  source: z.enum(ACTIVITY_SOURCES),
  channel: z.enum(CHANNELS).nullable(),
  referrer: z.string(),
  campaign: z.string(),
  landing: z.string(),
  country: z.string(),
  browser: z.string(),
  os: z.string(),
  language: z.string(),
});

const firstRewrite = z.object({
  kind: z.literal('first-rewrite'),
  at,
  words: count,
  tone: z.string(),
  afterMs: count.nullable().describe('How long after the first visit, if it’s known.'),
});

const firstVersion = z.object({
  kind: z.literal('first-version'),
  at,
  tier: RewritesSchema.keyof().nullable(),
  action: z.enum(VERSION_ACTIONS),
});

const days = z.object({
  kind: z.literal('days'),
  at,
  until: at,
  active: count.describe('Days with anything done.'),
  span: count.describe('Days from the first to the last, both included.'),
  rewrites: count,
  explanations: count,
  alternatives: count,
  tools: count,
  dictations: count,
  readAloud: count,
  tones: z.array(z.string()).describe('Tones other than the default, most used first.'),
});

const limit = z.object({
  kind: z.literal('limit'),
  at,
  signedIn: z.boolean().describe('Whether it was an account’s limit, rather than a visitor’s.'),
  opened: z.enum(PLANS_OPENERS).nullable().describe('Where the plans were opened from next.'),
});

const account = z.object({
  kind: z.literal('account'),
  at,
  method: z.enum(SIGN_IN_METHODS).nullable(),
});

const desktop = z.object({
  kind: z.literal('desktop'),
  at,
  platform: z.enum(DESKTOP_PLATFORMS).nullable().describe('The installer downloaded, if seen.'),
  system: z.enum(DESKTOP_SYSTEMS).nullable().describe('Where it first started, if it did.'),
  openedAfterMs: count.nullable().describe('From downloading to first starting, if both are seen.'),
});

const subscribed = z.object({
  kind: z.literal('subscribed'),
  at,
  plan: z.enum(PAID_PLAN_IDS).nullable(),
  interval: z.enum(BILLING_INTERVALS).nullable(),
  afterMs: count.nullable().describe('How long after the first visit, if it’s known.'),
});

const lastSeen = z.object({
  kind: z.literal('last-seen'),
  at,
  source: z.enum(ACTIVITY_SOURCES),
  client: UsedOnSchema.nullable(),
  rewritesThatDay: count,
});

export const JourneyItemSchema = z.discriminatedUnion('kind', [
  firstVisit,
  firstRewrite,
  firstVersion,
  days,
  limit,
  account,
  desktop,
  subscribed,
  lastSeen,
]);
export type JourneyItem = z.infer<typeof JourneyItemSchema>;

/** What an account's events say they used, besides what the account itself shows. */
export const JOURNEY_FEATURES = [
  'rewrite',
  'explanation',
  'alternatives',
  'tool',
  'dictation',
  'speech',
  'desktop',
  'api',
] as const;
export type JourneyFeature = (typeof JOURNEY_FEATURES)[number];

export const AdminJourneySchema = z.object({
  generatedAt: at,
  tracksVisitors: z.boolean(),
  keptSince: z.iso.date().describe('Events before this UTC day are gone.'),
  items: z.array(JourneyItemSchema).describe('Oldest first.'),
  facts: z
    .object({
      visits: count.describe('Visits and desktop app starts.'),
      rewrites: count.describe('Rewrites completed.'),
      clients: z
        .array(UsedOnSchema.extend({ source: z.enum(['web', 'desktop']) }))
        .describe('Browsers and desktop app versions it was used on, latest first.'),
      topTone: z.string().nullable(),
      topTier: RewritesSchema.keyof().nullable().describe('The version copied or inserted most.'),
      features: z.record(z.enum(JOURNEY_FEATURES), z.boolean()),
    })
    .nullable()
    .describe('Null when none of its events are kept.'),
});
export type AdminJourney = z.infer<typeof AdminJourneySchema>;
